import { getErrMsg } from '../lib/errorMsg';
import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { FileBarChart2, Play, ChevronDown, ChevronRight, AlertTriangle, TrendingUp, TrendingDown, Clock, RefreshCw, Users } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n ?? 0);
const fmtCur = (n) => `₹${fmt(n)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

function SectionBadge({ count, color }) {
  if (count === 0) return <span className="text-xs font-mono text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">0 — clear</span>;
  return <span className={`text-xs font-mono font-bold rounded-full px-2 py-0.5 ${color}`}>{count}</span>;
}

function PartTable({ parts, columns }) {
  if (!parts || parts.length === 0) return (
    <div className="text-center py-8 text-slate-400 text-sm">No parts in this section</div>
  );
  return (
    <div className="overflow-auto max-h-[70vh]">
      <table className="w-full text-sm border-collapse sticky-head">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {columns.map(c => (
              <th key={c.key} className={`px-4 py-2.5 font-semibold text-xs uppercase text-slate-500 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {parts.map((p, i) => (
            <tr key={i} className="hover:bg-slate-50 transition-colors">
              {columns.map(c => (
                <td key={c.key} className={`px-4 py-2.5 ${c.align === 'right' ? 'text-right' : ''}`}>
                  {c.render ? c.render(p) : <span className="text-slate-700 text-sm">{p[c.key] ?? '—'}</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReviewDetail({ review }) {
  const [tab, setTab] = useState('rm_behind');

  const tabs = [
    { key: 'rm_behind', label: 'RM Behind', count: review.summary?.rm_behind_count || 0, color: 'text-red-600' },
    { key: 'margin_risk', label: 'Margin at Risk', count: review.summary?.margin_at_risk_count || 0, color: 'text-amber-600' },
    { key: 'rm_changes', label: 'Significant RM Changes', count: review.summary?.rm_significant_changes_count || 0, color: 'text-blue-600' },
  ];

  const rmBehindCols = [
    { key: 'part_number', label: 'Part No.' },
    { key: 'part_name', label: 'Part Name' },
    { key: 'customer_name', label: 'Customer' },
    { key: 'material_grade', label: 'Grade' },
    { key: 'locked_rm_price', label: 'Locked RM', align: 'right', render: p => <span className="font-mono text-slate-700">{fmtCur(p.locked_rm_price)}</span> },
    { key: 'market_rm_price', label: 'Market RM', align: 'right', render: p => <span className="font-mono font-bold text-red-600">{fmtCur(p.market_rm_price)}</span> },
    { key: 'rm_excess', label: 'Excess/kg', align: 'right', render: p => <span className="font-mono font-bold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 text-xs">+{fmtCur(p.rm_excess)}</span> },
    { key: 'margin_pct', label: 'Margin %', align: 'right', render: p => <MarginBadge pct={p.margin_pct} /> },
  ];

  const marginCols = [
    { key: 'part_number', label: 'Part No.' },
    { key: 'part_name', label: 'Part Name' },
    { key: 'customer_name', label: 'Customer' },
    { key: 'material_grade', label: 'Grade' },
    { key: 'margin_pct', label: 'Margin %', align: 'right', render: p => <MarginBadge pct={p.margin_pct} /> },
    { key: 'selling_price', label: 'Sell Price', align: 'right', render: p => <span className="font-mono">{fmtCur(p.selling_price)}</span> },
    { key: 'total_manufacturing_cost', label: 'Mfg Cost', align: 'right', render: p => <span className="font-mono">{fmtCur(p.total_manufacturing_cost)}</span> },
  ];

  const rmChangesCols = [
    { key: 'category', label: 'Category' },
    { key: 'grade', label: 'Grade' },
    { key: 'old_price', label: 'Old Price', align: 'right', render: p => <span className="font-mono text-slate-500">{fmtCur(p.old_price)}</span> },
    { key: 'new_price', label: 'New Price', align: 'right', render: p => <span className="font-mono font-bold">{fmtCur(p.new_price)}</span> },
    { key: 'change_pct', label: 'Change', align: 'right', render: p => (
      <span className={`font-mono font-bold text-xs ${p.change_pct > 0 ? 'text-red-600' : 'text-green-600'}`}>
        {p.change_pct > 0 ? '+' : ''}{p.change_pct?.toFixed(2)}%
      </span>
    )},
  ];

  const currentData = tab === 'rm_behind' ? review.rm_behind_parts
    : tab === 'margin_risk' ? review.margin_at_risk_parts
    : review.rm_significant_changes;

  const currentCols = tab === 'rm_behind' ? rmBehindCols
    : tab === 'margin_risk' ? marginCols
    : rmChangesCols;

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="review-detail">
      {/* Tab row */}
      <div className="flex border-b border-slate-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`review-tab-${t.key}`}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
              tab === t.key ? 'border-slate-900 text-slate-900 bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            {t.label}
            <span className={`text-xs font-mono px-1.5 py-0.5 rounded-full ${
              t.count === 0 ? 'bg-slate-100 text-slate-400' : 'bg-red-50 text-red-600 font-bold'
            }`}>{t.count}</span>
          </button>
        ))}
      </div>
      <PartTable parts={currentData} columns={currentCols} />
    </div>
  );
}

function MarginBadge({ pct }) {
  if (pct >= 20) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">{(pct || 0).toFixed(1)}%</span>;
  if (pct >= 15) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">{(pct || 0).toFixed(1)}%</span>;
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">{(pct || 0).toFixed(1)}%</span>;
}

function ReviewRow({ review, expanded, onToggle }) {
  const s = review.summary || {};
  const hasIssues = (s.rm_behind_count || 0) + (s.margin_at_risk_count || 0) > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid={`review-row-${review.review_id}`}>
      <button onClick={onToggle} className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors">
        <div className="flex-1 min-w-0 grid grid-cols-2 lg:grid-cols-6 gap-4 items-center">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2">
              <Clock size={13} className="text-slate-400 flex-shrink-0" />
              <span className="text-sm font-semibold text-slate-800">{fmtDate(review.created_at)}</span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5 ml-5">
              {review.triggered_by === 'auto' ? 'Auto-scheduled' : `Manual — ${review.triggered_by}`}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 font-medium">Parts Reviewed</p>
            <p className="font-mono font-bold text-slate-900 text-lg">{review.total_parts}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 font-medium">RM Behind</p>
            <SectionBadge count={s.rm_behind_count || 0} color="bg-red-50 text-red-700 border border-red-200" />
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 font-medium">Margin at Risk</p>
            <SectionBadge count={s.margin_at_risk_count || 0} color="bg-amber-50 text-amber-700 border border-amber-200" />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasIssues && <span className="w-2 h-2 bg-red-500 rounded-full"></span>}
          {expanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
        </div>
      </button>
      {(s.rm_behind_count || 0) > 0 && s.rm_behind_customers_count != null && (
        <div className="px-5 py-2.5 bg-red-50/70 border-t border-red-100 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs"
          data-testid={`rm-behind-impact-${review.review_id}`}>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-300 font-semibold text-slate-700">
            <Users size={13} className="text-slate-500" />
            {s.rm_behind_customers_count} customer{s.rm_behind_customers_count !== 1 ? 's' : ''} affected
          </span>
          {s.rm_behind_top_customer && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600 text-white font-semibold"
              title="Customer with the largest market − locked RM gap">
              <TrendingUp size={13} />
              Highest lag: {s.rm_behind_top_customer}
              <span className="font-mono font-bold">+₹{Number(s.rm_behind_top_lag || 0).toFixed(2)}/kg</span>
            </span>
          )}
        </div>
      )}
      {expanded && <ReviewDetail review={review} />}
    </div>
  );
}

export default function Reports() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await api.get('/reports/market-review');
      setReviews(res.data);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReviews(); }, []);

  const handleRunReview = async () => {
    setRunning(true);
    try {
      const res = await api.post('/reports/market-review/run');
      toast.success(`Review complete — ${res.data.summary?.rm_behind_count || 0} RM behind, ${res.data.summary?.margin_at_risk_count || 0} margin at risk`);
      // Expand the newly created review
      setExpanded(res.data.review_id);
      // Merge full review into list
      setReviews(prev => [res.data, ...prev]);
    } catch (err) { toast.error(getErrMsg(err)); }
    finally { setRunning(false); }
  };

  const toggleExpand = async (review) => {
    if (expanded === review.review_id) {
      setExpanded(null);
      return;
    }
    // Fetch full review if not already loaded
    if (!review.rm_behind_parts) {
      try {
        const res = await api.get(`/reports/market-review/${review.review_id}`);
        setReviews(prev => prev.map(r => r.review_id === review.review_id ? res.data : r));
      } catch { toast.error('Failed to load review details'); return; }
    }
    setExpanded(review.review_id);
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-xl flex items-center gap-2">
            <FileBarChart2 size={20} className="text-orange-500" />
            Market Review Reports
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Auto-scheduled every 15 days · Compares market RM prices against locked costing prices
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchReviews} data-testid="refresh-reports"
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={handleRunReview} disabled={running} data-testid="run-review-btn"
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-60 transition-colors shadow-sm">
            {running ? (
              <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Running...</>
            ) : (
              <><Play size={13} /> Run Review Now</>
            )}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 flex flex-wrap gap-5 text-xs">
        <span className="flex items-center gap-1.5 text-red-600 font-semibold"><AlertTriangle size={12} /> RM Behind — Market RM ≥ ₹1/kg above locked costing price</span>
        <span className="flex items-center gap-1.5 text-amber-600 font-semibold"><TrendingDown size={12} /> Margin at Risk — Calculated margin below 15%</span>
        <span className="flex items-center gap-1.5 text-orange-600 font-semibold"><TrendingUp size={12} /> Cost Exceeded — Manufacturing cost exceeds approved costing price</span>
      </div>

      {/* Reviews list */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-slate-200 rounded-sm animate-pulse" />)}
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-16 text-center shadow-sm">
          <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileBarChart2 size={24} className="text-orange-500" />
          </div>
          <h3 className="font-heading font-bold text-slate-900 mb-2">No reviews yet</h3>
          <p className="text-slate-500 text-sm mb-5">Run your first market review to compare locked costing prices against current market rates.</p>
          <button onClick={handleRunReview} disabled={running} data-testid="run-review-empty-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 transition-colors">
            <Play size={13} /> Run First Review
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map(review => (
            <ReviewRow
              key={review.review_id}
              review={review}
              expanded={expanded === review.review_id}
              onToggle={() => toggleExpand(review)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
